import React from 'react'
import dynamic from 'next/dynamic'
import { Insight, InsightComponentProps } from 'src/components/insights/types'
import { FullScreenLoading } from 'src/components/full-screen-loading'
import { AreaVisitsIcon } from 'src/components/insights/icons/area-visits'
import InsightLocked from 'src/components/insight-locked'
import { useTier } from 'src/hooks/use-tier'
import { INSIGHTS_TIER } from 'src/constants/insights-tier'

import areaVisits1 from 'src/assets/images/insights-previews/area-visits/area_visits_1.gif'
import areaVisits1Placeholder from 'src/assets/images/insights-previews/area-visits/area_visits_1.png'
import areaVisits2 from 'src/assets/images/insights-previews/area-visits/area_visits_2.gif'
import areaVisits2Placeholder from 'src/assets/images/insights-previews/area-visits/area_visits_2.png'
import areaVisits3 from 'src/assets/images/insights-previews/area-visits/area_visits_3.gif'
import areaVisits3Placeholder from 'src/assets/images/insights-previews/area-visits/area_visits_3.png'
import { InsightContainer } from 'src/v2/views/owner-reveal/insights/container'
import { Colors } from 'src/components/theme'

const AreaVisitsContent = dynamic(() => import('src/components/insights/area-visits/content'), {
  loading: () => <FullScreenLoading />,
  ssr: false,
})

const AreaVisitsPage: React.FC<InsightComponentProps> = (props) => {
  const { insight } = props
  const meetsTier = useTier(INSIGHTS_TIER['Area Visits'])

  return (
    <InsightContainer insight={insight} color={Colors.purpleDark} downloadFileName="Area Visits">
      <React.Suspense fallback={<FullScreenLoading />}>
        {!meetsTier ? <InsightLocked insight={insight} /> : <AreaVisitsContent />}
      </React.Suspense>
    </InsightContainer>
  )
}

export const AreaVisits: Insight = {
  id: 'area-visits',
  component: AreaVisitsPage,
  name: 'Area Visits (Mobile Data)',
  icon: AreaVisitsIcon,
  supportedResultTypes: ['venue', 'address', 'area', 'alphamap-location'],
  linkTooltipFile: 'insights-link-area-visits',
  tier: INSIGHTS_TIER['Area Visits'],
  paywall: {
    title: 'See Area Visits Reports in Action',
    description: `Area Visits isn't available on your current plan. However, you can view full functionality of this feature by viewing an unlocked location or upgrading your plan below.`,
    synopsis: [
      {
        image: areaVisits1.src,
        imagePlaceholder: areaVisits1Placeholder.src,
        title: 'Optimize Business Locations',
        description:
          'Harness area visit data to strategically choose business sites, maximizing customer reach and engagement.',
      },
      {
        image: areaVisits2.src,
        imagePlaceholder: areaVisits2Placeholder.src,
        title: 'Unlock Market Dynamics',
        description:
          'Discover rich foot traffic insights to guide your market research and unveil consumer behavior trends.',
      },
      {
        image: areaVisits3.src,
        imagePlaceholder: areaVisits3Placeholder.src,
        title: 'Enhance Urban Planning',
        description:
          'Leverage pedestrian flow analytics for smarter urban development and impactful community planning.',
      },
    ],
  },
}
